import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router';
import { getPciPalAgentCookie } from './utils';

type AgentModeContext =
  | {
      isAgentMode: true;
      pciPalAgentId: number;
    }
  | {
      isAgentMode: false;
      pciPalAgentId?: never;
    };

const DEFAULT_ADMIN_MODE_CONTEXT: AgentModeContext = {
  isAgentMode: false,
  pciPalAgentId: undefined,
};

const AgentModeContext = createContext<AgentModeContext>(
  DEFAULT_ADMIN_MODE_CONTEXT
);

const MODE_PARAM = 'mode';
const AGENT_MODE_VALUE = 'agent';

export const AgentModeProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const [pciPalAgentId, setPciPalAgentId] = useState<number | undefined>();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasAgentModeQueryParam = params.get(MODE_PARAM) === AGENT_MODE_VALUE;
    const cookieValue = getPciPalAgentCookie();

    if (
      hasAgentModeQueryParam &&
      cookieValue &&
      cookieValue !== pciPalAgentId
    ) {
      setPciPalAgentId(cookieValue);
    }
  }, [location, pciPalAgentId, setPciPalAgentId]);

  return (
    <AgentModeContext.Provider
      value={
        {
          pciPalAgentId,
          isAgentMode: !!pciPalAgentId,
        } as AgentModeContext
      }
    >
      {children}
    </AgentModeContext.Provider>
  );
};

export const useAgentMode = () => {
  const context = useContext(AgentModeContext);
  if (!context) {
    throw new Error('useAgentMode must be used within an AgentModeProvider');
  }
  return context;
};
